class EuroCddStyles extends HTMLElement {
  connectedCallback() {
    this.mq = window.matchMedia('(min-width: 1200px)');
    this.tabs = Array.from(this.querySelectorAll('[data-cdd-styles-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-cdd-styles-panel]'));
    this.gallerySwipers = new Map();
    this.featureSwipers = new Map();
    this.tabsSwiper = null;
    this.tabsEl = this.querySelector('[data-cdd-styles-tabs-swiper]');

    this.onTabClick = (event) => {
      const index = Number(event.currentTarget.dataset.cddStylesTab);
      if (Number.isNaN(index)) return;
      this.selectStyle(index);
    };

    this.leaveTimer = null;

    this.onBreakpointChange = () => {
      this.destroyFeatureSwipers();
      this.initFeatureSwipers();
      this.initTabsSwiper();
      this.updateGalleryNav();
    };

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    this.mq.addEventListener('change', this.onBreakpointChange);

    this.whenReady(() => {
      this.initGallerySwipers();
      this.initFeatureSwipers();
      this.initTabsSwiper();
      this.activeIndex = -1;
      this.selectStyle(0, { animate: false });
      window.EuroAOS?.refresh();
    });
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
    this.mq?.removeEventListener('change', this.onBreakpointChange);
    window.clearTimeout(this.leaveTimer);
    clearInterval(this.readyTimer);
    this.destroyGallerySwipers();
    this.destroyFeatureSwipers();
    this.destroyTabsSwiper();
  }

  whenReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    this.readyTimer = setInterval(() => {
      if (!window.Swiper) return;
      clearInterval(this.readyTimer);
      callback();
    }, 30);
  }

  selectStyle(index, { animate = true } = {}) {
    if (this.activeIndex === index) return;

    const previousIndex = this.activeIndex;
    this.activeIndex = index;

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      panel.classList.remove('is-entering', 'is-leaving');

      if (!animate) return;

      if (active) {
        panel.classList.add('is-entering');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            panel.classList.remove('is-entering');
          });
        });
      } else if (i === previousIndex && previousIndex >= 0) {
        panel.classList.add('is-leaving');
        window.clearTimeout(this.leaveTimer);
        this.leaveTimer = window.setTimeout(() => {
          panel.classList.remove('is-leaving');
        }, 380);
      }
    });

    const gallery = this.gallerySwipers.get(index);
    if (gallery) {
      gallery.update();
      gallery.slideTo(0, 0);
    }

    const features = this.featureSwipers.get(index);
    if (features) {
      features.update();
      features.slideTo(0, 0);
    }

    // A panel that was hidden during init (inactive panels are
    // `visibility: hidden`) measured zero width, so Swiper needs a second
    // pass after the browser has laid the now-visible panel out.
    requestAnimationFrame(() => {
      this.gallerySwipers.get(index)?.update();
      this.featureSwipers.get(index)?.update();
    });

    this.updateGalleryNav();

    // Bring the freshly selected tab into view when the mobile row scrolls,
    // and re-measure since the row's content width may have changed.
    if (this.tabsSwiper) {
      this.tabsSwiper.update();
      this.tabsSwiper.slideTo(index);
    }

    if (animate) {
      const panel = this.panels[index];
      if (panel) window.EuroAOS?.replayIn(panel);
    }
  }

  /**
   * Mobile only: the tab row becomes a Swiper with `slidesPerView: 'auto'` so
   * each label keeps its natural width and the row scrolls horizontally.
   * Desktop relies on the plain flex row (CSS pins every slide to 25%), so the
   * Swiper is destroyed there to leave the static 4-per-page layout untouched.
   */
  initTabsSwiper() {
    this.destroyTabsSwiper();

    if (this.mq.matches || !window.Swiper || !this.tabsEl) return;

    const slides = this.tabsEl.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) return;

    this.tabsSwiper = new window.Swiper(this.tabsEl, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      speed: 320,
      watchOverflow: true,
      // Keep the active tab reachable when it is selected off-screen.
      slideToClickedSlide: false,
    });
  }

  destroyTabsSwiper() {
    if (!this.tabsSwiper) return;
    this.tabsSwiper.destroy(true, false);
    this.tabsSwiper = null;
  }

  initGallerySwipers() {
    this.destroyGallerySwipers();

    this.panels.forEach((panel, index) => {
      const el = panel.querySelector('[data-cdd-styles-gallery]');
      if (!el || !window.Swiper) return;

      const slides = el.querySelectorAll('.swiper-slide');
      if (!slides.length) return;

      const prev = panel.querySelector('[data-cdd-styles-prev]');
      const next = panel.querySelector('[data-cdd-styles-next]');
      const multi = slides.length > 1;

      const options = {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 450,
        watchOverflow: true,
        allowTouchMove: multi,
        // Swiper's navigation module is enabled by default, and its init hook
        // only short-circuits on a strict `enabled === false` check —
        // omitting the key or passing `undefined` is NOT a no-op (Swiper's
        // param merge keeps the defaults, so the module mounts with null
        // prevEl/nextEl and throws on init: "Cannot read properties of
        // undefined (reading 'enabled')"). Disable it explicitly for
        // single-image galleries.
        navigation: multi && prev && next
          ? {
              prevEl: prev,
              nextEl: next,
            }
          : { enabled: false },
      };

      const swiper = new window.Swiper(el, options);

      this.gallerySwipers.set(index, swiper);
    });

    this.updateGalleryNav();
  }

  updateGalleryNav() {
    this.panels.forEach((panel, index) => {
      const swiper = this.gallerySwipers.get(index);
      const nav = panel.querySelector('[data-cdd-styles-gallery-nav]');
      if (!nav) return;

      const count = swiper?.slides?.length || panel.querySelectorAll('[data-cdd-styles-gallery] .swiper-slide').length;
      nav.hidden = count <= 1;
    });
  }

  initFeatureSwipers() {
    this.destroyFeatureSwipers();
    if (this.mq.matches || !window.Swiper) return;

    this.panels.forEach((panel, index) => {
      const el = panel.querySelector('[data-cdd-styles-features-swiper]');
      if (!el) return;

      const pagination = panel.querySelector('[data-cdd-styles-features-dots]');
      const slides = el.querySelectorAll('.swiper-slide');
      if (slides.length <= 1) {
        if (pagination) pagination.hidden = true;
        return;
      }

      const options = {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 400,
        watchOverflow: true,
        // Same `enabled === false` rule as the gallery navigation: only wire
        // real elements, otherwise disable the module explicitly.
        pagination: pagination
          ? {
              el: pagination,
              clickable: true,
              bulletClass: 'euro-cdd-styles__dot',
              bulletActiveClass: 'is-active',
            }
          : { enabled: false },
      };

      const swiper = new window.Swiper(el, options);

      this.featureSwipers.set(index, swiper);
    });
  }

  destroyGallerySwipers() {
    this.gallerySwipers.forEach((swiper) => swiper.destroy(true, true));
    this.gallerySwipers.clear();
  }

  destroyFeatureSwipers() {
    this.featureSwipers.forEach((swiper) => swiper.destroy(true, true));
    this.featureSwipers.clear();
  }
}

if (!customElements.get('euro-cdd-styles')) {
  customElements.define('euro-cdd-styles', EuroCddStyles);
}
